import { Link, useRouterState } from '@tanstack/react-router';
import { cx } from '@/lib/cx';
import { Icon } from '@/components/ui';
import { useBalance, useMe, useUnreadAlerts, useUpdateProfile } from '@/api/hooks/core';
import { useResolvedTheme, useThemeStore } from '@/theme/theme';
import { env } from '@/lib/env';
import { int } from '@/lib/format';
import logoDark from '@/assets/text_logo_dark.png';
import logoLight from '@/assets/text_logo_light.png';
import s from './Rail.module.css';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Path prefixes that light this item up (detail pages belong to their section). */
  match: string[];
}

const NAV: NavItem[] = [
  { to: '/today', label: 'Today', icon: 'insights', match: ['/today'] },
  { to: '/strategies', label: 'Strategies', icon: 'tune', match: ['/strategies', '/templates'] },
  { to: '/watchlists', label: 'Watchlists', icon: 'bookmarks', match: ['/watchlists'] },
  { to: '/alerts', label: 'Alerts', icon: 'notifications', match: ['/alerts'] },
  { to: '/markets', label: 'Markets', icon: 'monitoring', match: ['/markets'] },
  { to: '/calendar', label: 'Calendar', icon: 'event', match: ['/calendar'] },
  {
    to: '/testing',
    label: 'Testing',
    icon: 'science',
    match: ['/testing', '/backtests', '/entry-checks', '/refine', '/parameter-searches', '/compare'],
  },
  { to: '/account', label: 'Account', icon: 'person', match: ['/account', '/plans'] },
];

const planName = (code: string | undefined) =>
  code ? code.charAt(0).toUpperCase() + code.slice(1) : '';

export function Rail() {
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const theme = useResolvedTheme();
  const setPreference = useThemeStore((st) => st.setPreference);
  const updateProfile = useUpdateProfile();
  const me = useMe().data;
  const balance = useBalance().data;
  const unread = useUnreadAlerts().data ?? 0;

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setPreference(next);
    // Saved to the account so the phone and other browsers follow.
    updateProfile.mutate({ theme_preference: next });
  };

  const initials = (me?.display_name ?? me?.email ?? '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  const credits = balance ? balance.remaining + (balance.bonus_remaining ?? 0) : null;

  return (
    <nav className={s.rail} aria-label="Main">
      <Link to="/today" className={s.logoLink} aria-label="SixOne8 — Today">
        <img src={theme === 'light' ? logoLight : logoDark} alt="" className={s.logo} />
      </Link>
      {env.envLabel && env.envLabel !== 'prod' && <span className={s.env}>{env.envLabel}</span>}
      {NAV.map((n) => {
        const active = n.match.some((m) => pathname === m || pathname.startsWith(`${m}/`));
        return (
          <Link key={n.to} to={n.to} className={cx(s.item, active && s.active)} aria-current={active ? 'page' : undefined}>
            <Icon name={n.icon} size={22} filled={active} />
            <span>{n.label}</span>
            {n.to === '/alerts' && unread > 0 && <span className={s.badge}>{unread > 99 ? '99+' : unread}</span>}
          </Link>
        );
      })}
      <div className={s.bottom}>
        <button type="button" className={s.theme} onClick={toggleTheme}>
          <Icon name={theme === 'light' ? 'dark_mode' : 'light_mode'} size={20} />
          {theme === 'light' ? 'Dark theme' : 'Light theme'}
        </button>
        <Link to="/account" className={s.account}>
          <span className={s.avatar}>{initials || '·'}</span>
          <span className={s.who}>
            <span className={s.name}>{me?.display_name ?? me?.email ?? ''}</span>
            <span className={s.plan}>
              {planName(me?.entitlements.plan_code)}
              {credits != null && ` · ${int(credits)} credits`}
            </span>
          </span>
        </Link>
      </div>
    </nav>
  );
}
